import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for managing meta tags, Open Graph, Twitter Cards, and Structured Data
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.keywords - SEO keywords (comma-separated)
 * @param {string} props.author - Content author
 * @param {string} props.image - Social media image URL
 * @param {string} props.url - Canonical URL
 * @param {string} props.type - Open Graph type (website, article, etc.)
 * @param {Object} props.structuredData - JSON-LD structured data
 * @param {string} props.twitterCard - Twitter card type (summary, summary_large_image)
 * @param {boolean} props.noIndex - Whether to prevent search engine indexing
 */
const SEO = ({
  title = 'SEQUILERN - POSH Compliance & Workplace Training',
  description = 'SEQUILERN provides comprehensive POSH (Prevention of Sexual Harassment) compliance training and workplace safety solutions.',
  keywords = 'POSH compliance, workplace training, sexual harassment prevention, workplace safety, compliance training, employee training',
  author = 'SEQUILERN',
  image = '/react-landing-pages-and-components-free-tailwindcss.png',
  url = typeof window !== 'undefined' ? window.location.href : 'https://sequilern.com',
  type = 'website',
  structuredData = null,
  twitterCard = 'summary_large_image',
  noIndex = false,
  children
}) => {
  // Construct full title
  const fullTitle = title.includes('SEQUILERN') ? title : `${title} | SEQUILERN`;
  
  // Ensure absolute URL for image
  const absoluteImage = image?.startsWith('http') 
    ? image 
    : `${typeof window !== 'undefined' ? window.location.origin : 'https://sequilern.com'}${image}`;

  // Default structured data for Organization
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SEQUILERN",
    "url": "https://sequilern.com",
    "logo": `${typeof window !== 'undefined' ? window.location.origin : 'https://sequilern.com'}/logo512.png`,
    "description": description,
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "email": "info@sequilern.com"
    },
    "sameAs": [
      "https://www.linkedin.com/company/sequilern",
      "https://twitter.com/sequilern"
    ]
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="SEQUILERN" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:site" content="@sequilern" />
      <meta name="twitter:creator" content="@sequilern" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      
      {/* Favicon and Apple Touch Icon */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo192.png" />
      
      {/* Theme Color */}
      <meta name="theme-color" content="#6415ff" />
      
      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
      
      {children}
    </Helmet>
  );
};

export default SEO;
